import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FuelConsumptionResponse } from './types';
import ChartErrorState from './ChartErrorState';
import './AttributedFuelTable.scss';

interface AttributedFuelTableProps {
  data: FuelConsumptionResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const formatNum = (value: number): string =>
  value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatShare = (value: number | null): string =>
  value === null ? '—' : `${value.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const AttributedFuelTable: FC<AttributedFuelTableProps> = ({ data, isLoading, error, onRetry }) => {
  const { t } = useTranslation();

  const title = t('cpi.analytics.attributedFuelConsumption');

  if (error && !isLoading) {
    return (
      <div className="attributed-fuel-table">
        <h3 className="attributed-fuel-table__title">{title}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="attributed-fuel-table attributed-fuel-table--loading">
        <h3 className="attributed-fuel-table__title">{title}</h3>
        <div className="attributed-fuel-table__skeleton" />
      </div>
    );
  }

  // `attributed` is absent when the API predates this field, so read it defensively
  // rather than letting the whole Energy tab crash on an older backend.
  if (!data?.attributed?.length) {
    return (
      <div className="attributed-fuel-table">
        <h3 className="attributed-fuel-table__title">{title}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="attributed-fuel-table">
      <h3 className="attributed-fuel-table__title">{title}</h3>
      <p className="attributed-fuel-table__subtitle">{t('cpi.analytics.attributedFuelSubtitle')}</p>
      <div className="attributed-fuel-table__wrapper">
        <table className="attributed-fuel-table__table">
          <thead>
            <tr>
              <th>{t('cpi.analytics.fuelType')}</th>
              <th>{t('cpi.analytics.quantity')}</th>
              <th>WTT</th>
              <th>TTW</th>
              <th>WTW</th>
              <th>{t('cpi.analytics.sharePct')}</th>
            </tr>
          </thead>
          <tbody>
            {data.attributed.map((row) => (
              <tr key={row.fuelType}>
                <td>{row.fuelType}</td>
                <td className="attributed-fuel-table__num">{formatNum(row.quantity)}</td>
                <td className="attributed-fuel-table__num">{formatNum(row.wttEmissionsKg)}</td>
                <td className="attributed-fuel-table__num">{formatNum(row.ttwEmissionsKg)}</td>
                <td className="attributed-fuel-table__num">{formatNum(row.wtwEmissionsKg)}</td>
                <td className="attributed-fuel-table__num attributed-fuel-table__share">
                  {formatShare(row.sharePct)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttributedFuelTable;
